import React, { createContext, useContext, memo } from 'react';
import Snackbar from '../../components/Snackbar/Snackbar';
import { SnackbarProviderProps, SnackbarContextType } from './types';
import { useSnackbarProvider } from './useSnackbarProvider';
import styles from './SnackbarProvider.module.css';

const MemoizedChildren = memo(({ children }: { children: React.ReactNode }) => <>{children}</>);
MemoizedChildren.displayName = 'MemoizedChildren';

// 空の処理を行うデフォルト関数
const noopSnackbar: SnackbarContextType = () => {};

// スナックバーコンテキストの作成（デフォルト値にnullの代わりにnoopSnackbarを使用）
const SnackbarContext = createContext<SnackbarContextType>(noopSnackbar);

/**
 * スナックバーを表示するためのフック
 * どのコンポーネントからでもスナックバーを表示できるようにする
 */
export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  return context;
};

/**
 * アプリケーション全体でスナックバーを管理・表示するためのプロバイダーコンポーネント
 * 子コンポーネントからuseSnackbarフックを使用してスナックバーを表示可能にする
 */
export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const { showSnackbar, snackbars, closeSnackbar } = useSnackbarProvider();

  return (
    <SnackbarContext.Provider value={showSnackbar}>
      <MemoizedChildren>{children}</MemoizedChildren>
      {/* 複数のスナックバーをレンダリング */}
      <div 
        className={styles['snackbar-provider-container']} 
        data-testid="snackbar-provider-container"
      >
        {snackbars.map((snackbar) => (
          <Snackbar
            key={snackbar.id}
            message={snackbar.message}
            open={snackbar.open}
            type={snackbar.type}
            onClose={() => closeSnackbar(snackbar.id)}
          />
        ))}
      </div>
    </SnackbarContext.Provider>
  );
}; 
